import { useQuery } from '@tanstack/react-query';
import { api, apiRequest, unwrap } from '@/api/client';
import { queryKeys } from '@/api/queryKeys';
import { useAuthStore } from '@/auth/authStore';
import { useTenantId } from '@/auth/useAuth';
import type { MetricsOverview } from './metrics';

/** Agents of the current tenant, for name lookups and filter dropdowns (null for platform users without a tenant). */
export function useAgentsLookup() {
  const tenantId = useTenantId();
  const query = useQuery({
    queryKey: queryKeys.scoped(tenantId, 'agents', 'lookup'),
    queryFn: async () => unwrap(await api.GET('/v1/agents', { params: { query: { limit: 200 } } })),
    enabled: tenantId !== null,
    staleTime: 60_000,
    meta: { silent: true },
  });
  const items = query.data?.items ?? [];
  const names = new Map(items.map((a) => [a.id, a.name] as const));
  return { agents: items, nameOf: (id: string | null | undefined) => (id ? (names.get(id) ?? id.slice(0, 8)) : '—') };
}

/** Metrics overview (07 §2.14). The response is untyped in OpenAPI because the endpoint also serves CSV. */
export async function fetchOverview(tenantId: string | null, range: { from?: string; to?: string } = {}): Promise<MetricsOverview> {
  return unwrap(await apiRequest<MetricsOverview>('GET', '/v1/metrics/overview', { query: { tenantId, ...range } }));
}

export function useOverview(range: { from?: string; to?: string } = {}) {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: queryKeys.metricsOverview(tenantId, range),
    queryFn: () => fetchOverview(tenantId, range),
    meta: { silent: true },
    retry: false,
  });
}

/** Open review/escalation counts for nav badges (shares the dashboard's overview query for today). */
export function useNavCounts(): Record<'reviews' | 'escalations', number> {
  const signedIn = useAuthStore((s) => s.accessToken !== null);
  const tenantId = useTenantId();
  const today = new Date().toISOString().slice(0, 10);
  const range = { from: today, to: today };
  const q = useQuery({
    queryKey: queryKeys.metricsOverview(tenantId, range),
    queryFn: () => fetchOverview(tenantId, range),
    enabled: signedIn,
    meta: { silent: true },
    retry: false,
    staleTime: 15_000,
  });
  return { reviews: q.data?.openReviews ?? 0, escalations: q.data?.openEscalations ?? 0 };
}
